import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '@/services/auth';

// Key to track if we're already processing a code
const PROCESSING_KEY = 'authCallbackProcessing';

export function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const processedRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double processing (React StrictMode, re-renders, etc.)
      if (processedRef.current) {
        return;
      }

      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (errorParam) {
        setError(errorDescription || errorParam || 'Authentication failed');
        return;
      }

      if (!code) {
        setError('No authorization code received');
        return;
      }

      // Check if this code was already processed (handles page refresh with same code)
      const processingCode = sessionStorage.getItem(PROCESSING_KEY);
      if (processingCode === code) {
        // This code was already attempted - if we're still here, it failed
        // Clear and show error to prevent infinite stuck state
        sessionStorage.removeItem(PROCESSING_KEY);
        setError('Authentication code has already been used. Please try signing in again.');
        return;
      }

      // Mark as processing
      processedRef.current = true;
      sessionStorage.setItem(PROCESSING_KEY, code);

      try {
        const success = await authService.exchangeCodeForTokens(code);
        // Clear processing flag
        sessionStorage.removeItem(PROCESSING_KEY);

        if (success) {
          // Redirect to home page after successful authentication
          navigate('/', { replace: true });
        } else {
          setError('Failed to exchange authorization code for tokens');
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        sessionStorage.removeItem(PROCESSING_KEY);
        setError('An error occurred during authentication');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="bg-white dark:bg-gray-800 py-8 px-6 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-red-600 dark:text-red-400 mb-4">
              <svg
                className="mx-auto h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Authentication Error
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="flex flex-col items-center space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
        <p className="text-gray-600 dark:text-gray-400">Completing sign in...</p>
      </div>
    </div>
  );
}
